import { ReactNode, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSessionTimeout } from "@/hooks/useSessionTimeout";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Upload, HelpCircle, BarChart3, LogOut, Briefcase, Home, Megaphone, Users, Shield, BookOpen, ShoppingCart, TrendingUp, Anchor, Ship, ClipboardCheck } from "lucide-react";

interface NavSection {
  section: string;
  items: { label: string; href: string; icon: any; permKey: string }[];
}

const allEmployeeNavSections: NavSection[] = [
  {
    section: "General",
    items: [
      { label: "Dashboard", href: "/employee", icon: Home, permKey: "page_dashboard" },
      { label: "Announcements", href: "/employee/announcements", icon: Megaphone, permKey: "page_announcements" },
      { label: "Directory", href: "/employee/directory", icon: BookOpen, permKey: "page_directory" },
      { label: "Upload Docs", href: "/employee/documents", icon: Upload, permKey: "page_documents" },
      { label: "Questions", href: "/employee/questions", icon: HelpCircle, permKey: "page_questions" },
      { label: "Progress", href: "/employee/progress", icon: BarChart3, permKey: "page_progress" },
    ],
  },
  {
    section: "Operations",
    items: [
      { label: "Crew Roster", href: "/employee/crew-roster", icon: Users, permKey: "page_crew_roster" },
      { label: "Safety & HSE", href: "/employee/safety", icon: Shield, permKey: "page_safety" },
      { label: "Internal Audits", href: "/employee/internal-audits", icon: ClipboardCheck, permKey: "page_safety" },
      { label: "Supply Chain", href: "/employee/supply-chain", icon: ShoppingCart, permKey: "page_supply_chain" },
    ],
  },
  {
    section: "Commercial",
    items: [
      { label: "Fleet Intel", href: "/employee/fleet-intelligence", icon: Anchor, permKey: "page_fleet_intel" },
      { label: "Vessel Catalogue", href: "/employee/vessel-catalogue", icon: Ship, permKey: "page_vessel_catalogue" },
    ],
  },
];

const EmployeeLayout = ({ children }: { children: ReactNode }) => {
  const { user, loading, signOut, isEmployee, isAdmin, fullName, employeePermissions } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  useSessionTimeout();

  const { data: avatarUrl } = useQuery({
    queryKey: ["layout-avatar", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("employee_directory")
        .select("avatar_url")
        .eq("user_id", user!.id)
        .maybeSingle();
      return data?.avatar_url || null;
    },
    enabled: !!user?.id,
  });

  const initials = (fullName || user?.email || "U").charAt(0).toUpperCase();

  const hasAccess = isEmployee || isAdmin;

  // Admins see all nav items; employees see only permitted ones
  const employeeNavSections = isAdmin
    ? allEmployeeNavSections
    : allEmployeeNavSections.map(section => ({
        ...section,
        items: section.items.filter(item => (employeePermissions as any)[item.permKey] !== false),
      })).filter(section => section.items.length > 0);

  useEffect(() => {
    if (!loading && !user) navigate("/employee-login");
    if (!loading && user && !hasAccess) navigate("/portal");
  }, [user, loading, hasAccess, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-navy-deep flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" />
      </div>
    );
  }

  if (!user || !hasAccess) return null;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <div className="bg-navy-deep border-b border-primary-foreground/5 mt-16 lg:mt-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between">
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
            <div className="flex items-center gap-1.5 pr-4 mr-2 border-r border-primary-foreground/10">
              <Briefcase className="w-3.5 h-3.5 text-lime" />
              <span className="text-[10px] font-bold tracking-[2px] uppercase text-lime font-body whitespace-nowrap">Employee</span>
            </div>
            {employeeNavSections.map((section) => (
              <div key={section.section} className="flex items-center">
                <div className="flex items-center gap-1 pr-2 mr-1 border-r border-primary-foreground/10">
                  <span className="text-[9px] font-bold tracking-[1.5px] uppercase text-primary-foreground/25 font-body whitespace-nowrap">{section.section}</span>
                </div>
                {section.items.map((item) => {
                  const active = location.pathname === item.href;
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.label}
                      to={item.href}
                      className={`flex items-center gap-2 px-3 py-4 text-[13px] font-body font-medium tracking-wide whitespace-nowrap transition-all duration-200 border-b-2 ${
                        active
                          ? "border-lime text-lime"
                          : "border-transparent text-primary-foreground/40 hover:text-primary-foreground/70"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3 py-3">
            <Link to="/employee/settings" className="flex items-center gap-2.5 group">
              <Avatar className="h-7 w-7 border border-primary-foreground/10 group-hover:border-teal transition-colors">
                {avatarUrl ? <AvatarImage src={avatarUrl} alt={fullName || "Profile"} /> : null}
                <AvatarFallback className="text-[10px] font-bold bg-teal/10 text-teal">{initials}</AvatarFallback>
              </Avatar>
              <p className="text-xs text-primary-foreground/40 font-body truncate max-w-[140px] group-hover:text-primary-foreground/60 transition-colors">{fullName || user?.email}</p>
            </Link>
            <button
              onClick={() => signOut().then(() => navigate("/employee-login"))}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-primary-foreground/30 hover:text-destructive hover:bg-destructive/5 transition-all font-body"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      <main className="flex-1 bg-navy-deep">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
          {children}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default EmployeeLayout;
